import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import i18n from '../i18n';
import { AUTH_INVALID_SESSION_EVENT, clearStoredAuthSession } from '../utils/authSessionSync';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      setToken(storedToken);
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          // First set user from localStorage immediately (preserving adminRole)
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Then validate token in background
          const response = await authService.validateToken();
          if (response.valid) {
            // Always trust server-returned adminRole (prevents privilege escalation)
            const serverUser = response.user;
            const finalUser = {
              ...serverUser,
              adminRole: serverUser.adminRole || 'user'
            };
            setUser(finalUser);
            localStorage.setItem('user', JSON.stringify(finalUser));
          } else {
            clearStoredAuthSession({ notify: true });
          }
        } catch (err) {
          console.error('Token validation error:', err);
          const status = err.response?.status;
          const msg = err.response?.data?.error || err.response?.data?.message;
          // Axios rejects 4xx; /auth/validate uses 403 for bad JWT — do not keep a dead session
          if (status === 401 || msg === 'Invalid or expired token') {
            clearStoredAuthSession({ notify: true });
          }
          // Other errors (network): keep cached user for offline-style UX
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    const onInvalidSession = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener(AUTH_INVALID_SESSION_EVENT, onInvalidSession);
    return () => window.removeEventListener(AUTH_INVALID_SESSION_EVENT, onInvalidSession);
  }, []);

  useEffect(() => {
    if (user?.appLanguage) {
      const code = String(user.appLanguage).split('-')[0];
      if (i18n.language && i18n.language.split('-')[0] !== code) {
        i18n.changeLanguage(code);
        localStorage.setItem('language', code);
      }
    }
  }, [user?.id, user?.appLanguage]);

  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      console.log('=== LOGIN ATTEMPT ===');
      console.log('Email:', email);
      const response = await authService.login(email, password, rememberMe);
      console.log('Login response:', response);
      
      if (response.token) {
        console.log('Login successful! Token received');
        // Clear any existing specialist session to prevent cross-auth contamination
        localStorage.removeItem('specialist_token');
        localStorage.removeItem('specialist');
        
        localStorage.setItem('token', response.token);
        setToken(response.token);
        
        // Store user without large base64 images to avoid localStorage quota issues
        const userToStore = { ...response.user };
        if (userToStore.companyLogo && userToStore.companyLogo.length > 1000) {
          // Store only the reference, not the full base64 image
          userToStore.companyLogo = userToStore.companyLogo.substring(0, 100) + '...truncated';
          userToStore.hasCompanyLogo = true;
        }
        if (userToStore.profileImage && userToStore.profileImage.length > 1000) {
          userToStore.profileImage = userToStore.profileImage.substring(0, 100) + '...truncated';
          userToStore.hasProfileImage = true;
        }
        
        localStorage.setItem('user', JSON.stringify(userToStore));
        setUser(response.user); // Keep full user in memory
        return { success: true, user: response.user };
      }
      
      console.log('Login failed: no token in response');
      return { success: false, error: response.error || 'Login failed' };
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (email, password, confirmPassword) => {
    try {
      setError(null);
      const response = await authService.register(email, password, confirmPassword);
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        setToken(response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Registration failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear ALL auth data to prevent session leakage (no event — logout already updates state)
      clearStoredAuthSession({ notify: false });
      localStorage.removeItem('specialist_token');
      localStorage.removeItem('specialist');
      setToken(null);
      setUser(null);
    }
  }, []);

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await authService.updateProfile(profileData);
      
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Profile update failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Profile update failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Refresh user data from localStorage or API
  const refreshUser = (newUserData) => {
    if (newUserData) {
      // Update React state immediately (for UI)
      setUser(newUserData);
      
      // For localStorage, handle base64 images carefully to avoid quota issues
      try {
        const userForStorage = { ...newUserData };
        // If logo/avatar is base64, don't store it in localStorage (too large)
        // Just keep it in React state for the current session
        if (userForStorage.companyLogo && userForStorage.companyLogo.startsWith('data:')) {
          userForStorage.companyLogo = null; // Don't persist base64 in localStorage
        }
        if (userForStorage.avatar && userForStorage.avatar.startsWith('data:')) {
          userForStorage.avatar = null; // Don't persist base64 in localStorage
        }
        localStorage.setItem('user', JSON.stringify(userForStorage));
      } catch (e) {
        console.warn('Could not save user to localStorage:', e);
        // Still keep user in React state even if localStorage fails
      }
    } else {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
  };

  const becomeSupplier = async () => {
    try {
      setError(null);
      const response = await authService.becomeSupplier();
      
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Failed to become supplier' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to become supplier. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const becomeBuyer = async () => {
    try {
      setError(null);
      const response = await authService.becomeBuyer();
      
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Failed to become buyer' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to become buyer. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      setError(null);
      const response = await authService.verifyEmail(email, code);
      
      if (response.user) {
        localStorage.setItem('token', response.token);
        setToken(response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        return { success: true, user: response.user };
      }
      
      return { success: false, error: response.error || 'Email verification failed' };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Email verification failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const clearError = () => {
    setError(null);
  };

  // Check if user has admin access
  const isAdmin = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin,
    isBuyer: user?.isBuyer || false,
    isSupplier: user?.isSupplier || false,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
    becomeSupplier,
    becomeBuyer,
    verifyEmail,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
